// src/pages/SignIn.js
import * as React from 'react';
import axios from 'axios';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import AppTheme from '../shared-theme/AppTheme';
import ForgotPassword from '../components/ForgotPassword';
import { API_BASE } from '../lib/api';
import { setTokens } from '../lib/auth';
import { authFieldSx } from './authFieldStyles';
import { passwordToggleSx } from './authPasswordToggleStyles';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: { maxWidth: 450 },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(4) },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignIn(props) {
  const navigate = useNavigate();

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [apiError, setApiError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  // Forgot password dialog
  const [openForgot, setOpenForgot] = React.useState(false);

  const validateInputs = () => {
    const email = document.getElementById('email');
    const password = document.getElementById('password');

    let isValid = true;

    if (!email?.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password?.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError('');
    if (!validateInputs()) return;

    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');

    try {
      setSubmitting(true);
      // Χρησιμοποιούμε direct axios γιατί εδώ ΔΕΝ έχουμε token ακόμη
      const res = await axios.post(`${API_BASE}/login`, {
        username: email,
        password,
      });

      if (res.status === 200) {
        const { access_token, refresh_token } = res.data || {};
        if (access_token && refresh_token) {
          setTokens({ access: access_token, refresh: refresh_token });
        }

        // 🔥🔥 SAVE THE USER
        if (res.data.user) {
          localStorage.setItem("user", JSON.stringify(res.data.user));
        }

        navigate('/');
      }
      else {
        setApiError('Login failed. Please try again.');
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Invalid credentials.';

      // Αν ο λογαριασμός δεν είναι verified, στείλε στη σελίδα OTP
      if (status === 403 && /not verified|OTP/i.test(msg)) {
        navigate(`/verify-otp?email=${encodeURIComponent(email)}`);
        return;
      }

      setPasswordError(true);
      setPasswordErrorMessage(msg);
      setApiError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src="/images/seedsLogo.png"
              alt="SEEDS Logo"
              style={{ maxHeight: 80, objectFit: 'contain' }}
            />
          </Box>

          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            textAlign="center"
          >
            Sign in
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={emailError ? 'error' : 'primary'}
                sx={authFieldSx}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
                sx={authFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        edge="end"
                        onClick={() => setShowPassword((value) => !value)}
                        onMouseDown={(event) => event.preventDefault()}
                        sx={passwordToggleSx}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <ForgotPassword open={openForgot} handleClose={() => setOpenForgot(false)} />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting}
              sx={{
                "&&": {
                  backgroundColor: "#f7b900",
                  backgroundImage: "none",
                  color: "#171717",
                  borderColor: "#f7b900",
                  boxShadow: "none",
                },
                "&&:hover, &&:focus-visible": {
                  backgroundColor: "#d99b00",
                  backgroundImage: "none",
                  color: "#171717",
                  borderColor: "#d99b00",
                  boxShadow: "none",
                },
                fontWeight: 700,
              }}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <Link
              component="button"
              type="button"
              onClick={() => setOpenForgot(true)}
              variant="body2"
              underline="none"
              sx={{ alignSelf: 'center', color: '#9a7000', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#9a7000', textDecoration: 'none' } }}
            >
              Forgot your password?
            </Link>

            {apiError && <Alert severity="error">{apiError}</Alert>}
          </Box>

          <Typography sx={{ textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to="/signup"
              underline="none"
              sx={{ color: '#9a7000', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#9a7000', textDecoration: 'none' } }}
            >
              Don’t have an account? Sign Up
            </Link>
          </Typography>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}
